import { useMemo, useRef } from "react";

import { CommunicationPreferenceFormType } from "@simplyk/common";
import { enqueueSnackbar } from "notistack";

import { getFormCommunicationPreferencesMutationScope } from "./formCommunicationPreferencesMutationScope";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { trpc } from "@/common/helpers/trpc";
import { useTranslate } from "@/common/hooks/useTranslate";
import { useCustomQuestionsTracking } from "@/features/LiveFormEditor/Steps/StepQuestionsV4/helpers/useCustomQuestionsTracking";
import {
  AddQuestionSelection,
  BuyerPresetOption,
  COMMUNICATION_PREFERENCES_OPTION_ID,
} from "@/features/LiveFormEditor/Steps/StepQuestionsV4/types";

interface UseCommunicationPreferencesPresetOptions {
  formId: string;
  formType: CommunicationPreferenceFormType;
  isEnabled: boolean;
  onAdded?: () => void;
  onRemoved?: () => void;
}

/** Offers communication preferences as a row in the add-question modal, for a form that does not have them yet. */
export const useCommunicationPreferencesPreset = ({
  formId,
  formType,
  isEnabled,
  onAdded,
  onRemoved,
}: UseCommunicationPreferencesPresetOptions) => {
  const { t } = useTranslate();
  const utils = trpc.useUtils();
  const logCustomQuestionsEvent = useCustomQuestionsTracking();

  const { data } = trpc.getFormCommunicationPreferences.useQuery({ formId, formType }, { enabled: isEnabled });
  const { mutateAsync: ensureDefaultCommunicationPreference, isPending: isEnsuring } =
    trpc.ensureDefaultCommunicationPreference.useMutation();
  const { mutateAsync: updateFormCommunicationPreferences } = trpc.updateFormCommunicationPreferences.useMutation({
    scope: getFormCommunicationPreferencesMutationScope(formId, formType),
  });

  const defaultOptionName = t("dashboard", "communicationPreferences.defaultOptionName");
  const organizationOptions = data?.options ?? [];
  const isAlreadyOnForm = (data?.selectedContactListIds.length ?? 0) > 0;

  // Adding the row attaches every option the organization has, the same way a new form inherits them.
  const attachedNames =
    organizationOptions.length > 0 ? organizationOptions.map((option) => option.name) : [defaultOptionName];

  const subtitle = attachedNames.join(t("dashboard", "communicationPreferences.separator"));
  const label = t("dashboard", "communicationPreferences.questionTitle");
  // Creating the first option is not shown ahead of the server, so a second confirm would create a second option.
  const isOffered = isEnabled && Boolean(data) && !isAlreadyOnForm && !isEnsuring;

  // Memoized: the modal keys its option list off this array's identity.
  const presets: BuyerPresetOption[] = useMemo(
    () => (isOffered ? [{ id: COMMUNICATION_PREFERENCES_OPTION_ID, label, subtitle }] : []),
    [isOffered, label, subtitle]
  );

  const formPreferencesKey = { formId, formType };
  const latestSaveIdRef = useRef(0);

  const reportSaveError = () =>
    enqueueSnackbar(t("dashboard", "communicationPreferences.saveError"), { vibe: "danger" });

  // Shows the new selection before the server confirms it, like other questions, and restores it if the save fails.
  const saveSelection = async (contactListIds: string[], onShown?: () => void) => {
    latestSaveIdRef.current += 1;
    const saveId = latestSaveIdRef.current;
    // A newer save already shows its own selection, so only the latest one may write the cache.
    const isLatestSave = () => saveId === latestSaveIdRef.current;
    const previous = utils.getFormCommunicationPreferences.getData(formPreferencesKey);
    const restorePrevious = () => {
      if (isLatestSave()) {
        utils.getFormCommunicationPreferences.setData(formPreferencesKey, previous);
      }
    };
    if (previous) {
      utils.getFormCommunicationPreferences.setData(formPreferencesKey, {
        ...previous,
        selectedContactListIds: contactListIds,
      });
    }
    onShown?.();

    const { data: formPreferences, error } = await updateFormCommunicationPreferences(
      { formId, formType, contactListIds },
      { onError: restorePrevious }
    );
    if (error || !formPreferences) {
      restorePrevious();
      reportSaveError();
      return false;
    }

    if (isLatestSave()) {
      utils.getFormCommunicationPreferences.setData(formPreferencesKey, formPreferences);
    }
    return true;
  };

  // The organization's very first option has to be created server-side: only it can find a free list name.
  const createDefaultOption = async () => {
    const { data: formPreferences, error } = await ensureDefaultCommunicationPreference({
      formId,
      formType,
      defaultName: defaultOptionName,
    });
    if (error || !formPreferences) {
      reportSaveError();
      return false;
    }

    utils.getFormCommunicationPreferences.setData(formPreferencesKey, formPreferences);
    void utils.getCommunicationPreferenceOptions.invalidate();
    void utils.getContactLists.invalidate();
    onAdded?.();
    return true;
  };

  const addFromSelection = async (selection: AddQuestionSelection) => {
    if (!selection.buyerPresetIds.includes(COMMUNICATION_PREFERENCES_OPTION_ID) || isEnsuring) {
      return;
    }

    const isSaved =
      organizationOptions.length > 0
        ? await saveSelection(
            organizationOptions.map((option) => option.contactListId),
            onAdded
          )
        : await createDefaultOption();
    if (!isSaved) {
      return;
    }

    logCustomQuestionsEvent(AmplitudeEvents.CommunicationPreferencesConfigured, {
      count_options: attachedNames.length,
      entry_point: "live_form_editor",
    });
  };

  const removeFromForm = async () => {
    if (!(await saveSelection([], onRemoved))) {
      return;
    }

    logCustomQuestionsEvent(AmplitudeEvents.CommunicationPreferencesConfigured, {
      count_options: 0,
      entry_point: "live_form_editor",
    });
  };

  return { presets, addFromSelection, removeFromForm, isOfferedOnForm: isAlreadyOnForm };
};
